import { useState } from "react";
import { Download, FileText, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n";
import { useData } from "@/context/DataContext";
import { UploadDropzone, type PickedFile } from "@/components/UploadDropzone";
import { Badge, Button, Card, EmptyState, SectionHeading } from "@/components/ui";
import type { DocType, UploadedDocument } from "@/lib/mock";
import { formatBytes, formatDate } from "@/lib/format";

const typeToKey: Record<DocType, "docs.type_cedula" | "docs.type_rut" | "docs.type_camara_comercio" | "docs.type_poder" | "docs.type_otro"> = {
  cedula: "docs.type_cedula",
  rut: "docs.type_rut",
  camara_comercio: "docs.type_camara_comercio",
  poder: "docs.type_poder",
  otro: "docs.type_otro",
};

export function Documents() {
  const { t, locale } = useT();
  const { data, addDocument, removeDocument, pushActivity } = useData();
  const [uploading, setUploading] = useState<PickedFile | null>(null);
  const [newDocType, setNewDocType] = useState<DocType>("cedula");

  const handleAdd = () => {
    if (!uploading) return;
    const doc: UploadedDocument = {
      id: `d_${Date.now()}`,
      name: uploading.name,
      type: newDocType,
      sizeBytes: uploading.sizeBytes,
      uploadedAt: new Date().toISOString(),
    };
    addDocument(doc);
    pushActivity({
      id: `a_${Date.now()}`,
      at: new Date().toISOString(),
      icon: "upload",
      text: `Subiste ${doc.name}`,
    });
    setUploading(null);
    toast.success(t("status.uploaded"), { description: doc.name });
  };

  const handleDelete = (id: string) => {
    removeDocument(id);
    toast.message("Documento eliminado");
  };

  return (
    <div className="space-y-8">
      <SectionHeading title={t("docs.title")} subtitle={t("docs.subtitle")} />

      <div className="grid gap-6 md:grid-cols-[1.2fr_1fr]">
        <Card className="p-6">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            {t("docs.upload")}
          </h3>

          <div className="mt-5">
            {!uploading ? (
              <UploadDropzone value={null} onChange={setUploading} />
            ) : (
              <div className="space-y-4">
                <UploadDropzone value={uploading} onChange={setUploading} />
                <label className="block">
                  <span className="label">Tipo</span>
                  <select
                    className="input mt-1.5"
                    value={newDocType}
                    onChange={(e) => setNewDocType(e.target.value as DocType)}
                  >
                    <option value="cedula">{t("docs.type_cedula")}</option>
                    <option value="rut">{t("docs.type_rut")}</option>
                    <option value="camara_comercio">{t("docs.type_camara_comercio")}</option>
                    <option value="poder">{t("docs.type_poder")}</option>
                    <option value="otro">{t("docs.type_otro")}</option>
                  </select>
                </label>
                <Button onClick={handleAdd}>
                  <Upload className="h-4 w-4" />
                  {t("docs.upload")}
                </Button>
              </div>
            )}
          </div>
        </Card>

        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Tus documentos quedan cifrados y sólo tú y nuestro equipo los ven.
          </p>
          <ul className="grid grid-cols-2 gap-2 text-xs text-slate-600">
            <li className="rounded-xl border border-slate-200 bg-white px-3 py-2">PDF</li>
            <li className="rounded-xl border border-slate-200 bg-white px-3 py-2">JPG / PNG</li>
            <li className="rounded-xl border border-slate-200 bg-white px-3 py-2">Máx. 10 MB</li>
            <li className="rounded-xl border border-slate-200 bg-white px-3 py-2">Cifrado en reposo</li>
          </ul>
        </div>
      </div>

      {data.documents.length === 0 ? (
        <EmptyState
          icon={<FileText className="h-6 w-6" />}
          title={t("docs.empty_title")}
          body={t("docs.empty_body")}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {data.documents.map((d) => (
            <Card key={d.id} className="flex items-start gap-4 p-4">
              <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                <FileText className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Badge tone="brand">{t(typeToKey[d.type])}</Badge>
                </div>
                <p className="mt-2 truncate text-sm font-semibold text-slate-900">{d.name}</p>
                <p className="text-xs text-slate-500">
                  {formatBytes(d.sizeBytes)} · {t("docs.uploaded_on")} {formatDate(d.uploadedAt, locale)}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <button
                  className="btn-ghost h-8 w-8 p-0"
                  aria-label={t("common.download")}
                  onClick={() => toast.message("Descargando…")}
                >
                  <Download className="h-4 w-4" />
                </button>
                <button
                  className="btn-ghost h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                  aria-label="Eliminar"
                  onClick={() => handleDelete(d.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
